import { map } from 'rxjs';
import type { Observable } from 'rxjs';
import { keyBy } from 'lodash';

import { StaticInfoSearchService } from '@apps/main/modules/search/staticInfo/staticInfoSearchService';

import type {
  DestinationAvailabilityCriteria,
  ItinerarySearchCriteria,
  ActivityAvailabilityResponse,
  ITripSearchService,
  DestinationAvailabilityResponse,
  RecipeResponse,
  CarrierName,
  CarrierStaticInfo,
  PortStaticInfo,
  OverviewStaticInfoResult,
  DestinationStaticInfo,
  DetailsStaticInfo,
  ActivityAvailabilityCriteria,
  HotelConstraintQL,
  HotelIngredientAdapter,
  TransportVehicle,
  TransportAvailabilityCriteria,
  TransportListResponse,
  AccommodationAvailabilityCriteria,
  Coordinate,
  IngredientId,
  ActivitySuggestionsResponse,
  ActivityStaticInfo,
  ActivitySuggestionsSearchCriteria,
  HotelBookableOptionWithRoomsAndDateAndPrices,
  ItinerarySuggestions,
  NeighborhoodPolygonsOnlyHotel,
} from '@libs/trip-planner-interfaces';
import {
  ActivitySuggestionService,
  DestinationAvailabilitySearchService,
  ItinerarySearchService,
  ProductAvailabilitySearchService,
} from '@libs/search-service-smart-search';

export class SmartSearchService implements ITripSearchService {
  private staticInfoSearchService = new StaticInfoSearchService();
  private activitySuggestionService = new ActivitySuggestionService();
  private itinerarySearchService = new ItinerarySearchService();
  private productAvailabilitySearchService =
    new ProductAvailabilitySearchService();
  private destinationAvailabilitySearchService =
    new DestinationAvailabilitySearchService();

  public searchItineraries(criteria: ItinerarySearchCriteria): RecipeResponse {
    return this.itinerarySearchService.search(criteria);
  }

  public searchItinerarySuggestions(
    criteria: ItinerarySearchCriteria
  ): ItinerarySuggestions {
    return this.itinerarySearchService.searchSuggestions(criteria);
  }

  public searchDestinationAvailability(
    criteria: DestinationAvailabilityCriteria
  ): DestinationAvailabilityResponse {
    return this.destinationAvailabilitySearchService.search(criteria);
  }

  public searchOverviewStaticInfo(
    ingredients: HotelIngredientAdapter[]
  ): Observable<OverviewStaticInfoResult> {
    return this.staticInfoSearchService.searchOverview(ingredients);
  }

  public searchDestinationStaticInfo(
    ingredient: HotelIngredientAdapter
  ): Observable<Record<string, DestinationStaticInfo>> {
    return this.staticInfoSearchService.searchDestination(ingredient);
  }

  public searchMinimumActivityStaticInfo(
    constraint: HotelConstraintQL
  ): Observable<boolean> {
    return this.staticInfoSearchService.searchMinimumActivityStaticInfo(
      constraint
    );
  }

  public getActivitySuggestions(
    criteria: ActivitySuggestionsSearchCriteria
  ): Promise<ActivitySuggestionsResponse> {
    return this.activitySuggestionService.getSuggestions(criteria);
  }

  public getActivitiesStaticInfo(
    codes: string[]
  ): Observable<ActivityStaticInfo[]> {
    return this.staticInfoSearchService.getActivitiesStaticInfo(codes);
  }

  public searchDestinationSuggestedActivities(
    constraint: HotelConstraintQL
  ): Observable<ActivityStaticInfo[]> {
    return this.staticInfoSearchService.searchDestinationSuggestedActivities(
      constraint
    );
  }

  public searchDetailsStaticInfo(
    hotelCode: string,
    ingredientId: IngredientId
  ): Observable<DetailsStaticInfo> {
    return this.staticInfoSearchService.searchDetails(hotelCode, ingredientId);
  }

  public searchHotelName(inputText: string): Observable<string> {
    return this.staticInfoSearchService.searchHotelName(inputText);
  }

  public searchHotelCode(hotelName: string): Observable<string | undefined> {
    return this.staticInfoSearchService.searchHotelCode(hotelName);
  }

  public searchHotelNearbyPolygon(
    inputText: string
  ): Observable<{ name?: string; id?: number }> {
    return this.staticInfoSearchService.searchHotelNearbyPolygon(inputText);
  }

  public searchHotelPosition(
    inputText: string
  ): Observable<Coordinate | undefined> {
    return this.staticInfoSearchService.searchHotelPosition(inputText);
  }

  public searchHotelNeighborhoodPolygons(
    inputText: string
  ): Observable<NeighborhoodPolygonsOnlyHotel[] | undefined> {
    return this.staticInfoSearchService.searchHotelNeighborhoodPolygons(
      inputText
    );
  }

  public searchCarriersStaticInfo(
    uniqCarrierCodes: string[]
  ): Observable<Record<string, CarrierStaticInfo>> {
    return this.staticInfoSearchService
      .searchCarriersStaticInfo(uniqCarrierCodes)
      .pipe(map((carriers) => keyBy(carriers, 'code')));
  }

  public getCachedCarriers(
    carrierCodes: string[]
  ): Record<string, CarrierStaticInfo> {
    return keyBy(
      this.staticInfoSearchService.getCachedCarriers(carrierCodes),
      'code'
    );
  }

  public searchCarrierNames(
    carrierCodes: string[]
  ): Observable<Record<string, CarrierName>> {
    return this.staticInfoSearchService.searchCarrierNames(carrierCodes);
  }

  public searchAirportStaticInfo(iataCode: string): Observable<PortStaticInfo> {
    return this.staticInfoSearchService.searchAirportStaticInfo(iataCode);
  }

  public searchSeaportStaticInfo(portCode: string): Observable<PortStaticInfo> {
    return this.staticInfoSearchService.searchSeaportStaticInfo(portCode);
  }

  public searchAccommodationAvailability(
    criteria: AccommodationAvailabilityCriteria
  ): Observable<HotelBookableOptionWithRoomsAndDateAndPrices> {
    return this.productAvailabilitySearchService.searchAccommodationProductAvailability(
      criteria
    );
  }

  public searchActivityAvailability(
    ticketsRquest: ActivityAvailabilityCriteria
  ): Observable<ActivityAvailabilityResponse> {
    return this.productAvailabilitySearchService.searchActivityProductAvailability(
      ticketsRquest
    );
  }

  public searchTransportAvailability(
    transportAvailabilityCriteria: TransportAvailabilityCriteria
  ): Observable<TransportListResponse> {
    return this.productAvailabilitySearchService.searchTransportProductAvailability(
      transportAvailabilityCriteria
    );
  }

  public getFerryVehicleTypes(): Observable<TransportVehicle[]> {
    return this.staticInfoSearchService.getFerryVehicleTypes();
  }
}
